import React, { useState, useEffect, useCallback } from 'react';
import { Target, TrendingUp, Users, Edit2 } from 'lucide-react';
import { useFM } from '@/context/FMContext';
import { axiosInstance } from '@/App';
import ProgressBarWithLabel from '@/components/fm/ProgressBarWithLabel';
import { formatCurrency } from '@/lib/fmUtils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { toast } from 'sonner';

// Default rank-based weightages (from Operations Bible)
const DEFAULT_RANK_WEIGHTAGES = { 1: 30, 2: 27, 3: 23, 4: 20 };

/**
 * FMRevenueTargets - Revenue targets with operating cost, PT target,
 * overall progress bar, trainer breakdown cards, and weightage editor.
 */
export default function FMRevenueTargets() {
  const { selectedGymId } = useFM();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  const canEditWeightages = user.role === 'admin' || user.role === 'fitness_manager' || user.role === 'manager';

  const [targets, setTargets] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit modals
  const [editCostOpen, setEditCostOpen] = useState(false);
  const [editWeightagesOpen, setEditWeightagesOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (!selectedGymId) {
      setTargets(null);
      setProgress(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [targetsRes, progressRes] = await Promise.allSettled([
        axiosInstance.get(`/fm/targets/${selectedGymId}`),
        axiosInstance.get(`/fm/targets/${selectedGymId}/progress`),
      ]);

      if (targetsRes.status === 'fulfilled') {
        setTargets(targetsRes.value.data);
      } else {
        throw targetsRes.reason;
      }

      if (progressRes.status === 'fulfilled') {
        setProgress(progressRes.value.data);
      }
    } catch (err) {
      console.error('Failed to fetch targets:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load targets');
    } finally {
      setLoading(false);
    }
  }, [selectedGymId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // No gym selected
  if (!selectedGymId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Target className="w-12 h-12 text-slate-300 mb-3" />
        <p className="text-sm text-slate-500">Select a gym to view revenue targets</p>
      </div>
    );
  }

  const operatingCost = targets?.operating_cost || 0;
  const ptTarget = targets?.pt_target || 0;
  const trainerTargets = targets?.trainer_targets || [];
  const totalRevenue = progress?.total_revenue || 0;
  const freshRevenue = progress?.fresh_revenue || 0;
  const renewalRevenue = progress?.renewal_revenue || 0;
  const progressPct = progress?.progress_pct || 0;

  return (
    <PullToRefresh onRefresh={fetchData} pullingContent="" refreshingContent={<RefreshingSpinner />}>
      <div className="space-y-4 pb-8">
        {/* Page header */}
        <div>
          <h2 className="text-lg font-bold text-slate-800">Revenue Targets</h2>
          <p className="text-xs text-slate-500 mt-0.5">Track PT revenue against monthly targets</p>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-lg border p-4 bg-white animate-pulse">
                <div className="h-5 bg-slate-200 rounded w-1/2 mb-3" />
                <div className="h-4 bg-slate-100 rounded w-2/3" />
              </div>
            ))}
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <p className="text-sm text-red-700">{error}</p>
            <button onClick={fetchData} className="mt-2 text-xs text-red-600 underline">
              Retry
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* 1. Operating cost display */}
            <div className="rounded-xl border bg-white p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-700">Monthly Operating Cost</h3>
                {isAdmin && (
                  <button
                    onClick={() => setEditCostOpen(true)}
                    className="text-xs text-blue-600 flex items-center gap-1 active:scale-95 transition-transform"
                  >
                    <Edit2 className="w-3 h-3" />
                    Edit
                  </button>
                )}
              </div>
              <p className="text-2xl font-bold text-slate-800">{formatCurrency(operatingCost)}</p>
              <p className="text-xs text-slate-500 mt-1">
                PT Target: {formatCurrency(ptTarget)} (2x Operating Cost)
              </p>
            </div>

            {/* 2. Overall progress bar */}
            <div className="rounded-xl border bg-white p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Overall Revenue Progress</h3>
              <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                <span className="font-medium">
                  {formatCurrency(totalRevenue)} / {formatCurrency(ptTarget)}
                </span>
                <span
                  className={`font-bold ${
                    progressPct >= 70
                      ? 'text-green-600'
                      : progressPct >= 40
                      ? 'text-yellow-600'
                      : 'text-red-600'
                  }`}
                >
                  {Math.round(progressPct)}%
                </span>
              </div>
              {/* Split visualization bar: Fresh + Renewal */}
              <div className="h-3 rounded-full bg-slate-200 overflow-hidden flex">
                {freshRevenue > 0 && ptTarget > 0 && (
                  <div
                    className="h-full bg-green-500 transition-all duration-500"
                    style={{ width: `${Math.min((freshRevenue / ptTarget) * 100, 100)}%` }}
                  />
                )}
                {renewalRevenue > 0 && ptTarget > 0 && (
                  <div
                    className="h-full bg-purple-500 transition-all duration-500"
                    style={{ width: `${Math.min((renewalRevenue / ptTarget) * 100, 100)}%` }}
                  />
                )}
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-400">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                  Fresh {formatCurrency(freshRevenue)}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-purple-500 inline-block" />
                  Renewal {formatCurrency(renewalRevenue)}
                </span>
              </div>
            </div>

            {/* 3. Trainer breakdown cards */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-slate-400" />
                  Trainer Breakdown
                </h3>
                {canEditWeightages && (
                  <button
                    onClick={() => setEditWeightagesOpen(true)}
                    className="text-xs text-blue-600 flex items-center gap-1 active:scale-95 transition-transform"
                  >
                    <Edit2 className="w-3 h-3" />
                    Edit Weightages
                  </button>
                )}
              </div>

              {trainerTargets.length === 0 && (
                <div className="rounded-lg border bg-white p-6 text-center">
                  <p className="text-sm text-slate-400">No active trainers in this gym</p>
                </div>
              )}

              <div className="space-y-3">
                {[...trainerTargets]
                  .sort((a, b) => (a.rank || 99) - (b.rank || 99))
                  .map((trainer) => {
                    const trainerPct =
                      trainer.target > 0
                        ? Math.min((trainer.achieved / trainer.target) * 100, 100)
                        : 0;

                    return (
                      <div
                        key={trainer.trainer_id}
                        className="rounded-xl border bg-white p-4"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-semibold text-slate-700">
                            {trainer.trainer_name || 'Unknown'}
                            {trainer.rank > 0 && (
                              <span className="text-xs text-slate-400 ml-1">(Rank {trainer.rank})</span>
                            )}
                          </span>
                          <span className="text-xs font-medium text-slate-500">
                            {trainer.weightage}%
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mb-1">
                          Target: {formatCurrency(trainer.target)}
                        </p>
                        <p className="text-xs text-slate-600 mb-2">
                          Achieved: {formatCurrency(trainer.achieved)} ({Math.round(trainerPct)}%)
                        </p>
                        <ProgressBarWithLabel
                          value={trainer.achieved}
                          max={trainer.target}
                          format="currency"
                        />
                      </div>
                    );
                  })}
              </div>
            </div>
          </>
        )}

        {/* Operating Cost Edit Dialog (admin only) */}
        <EditOperatingCostDialog
          open={editCostOpen}
          onOpenChange={setEditCostOpen}
          gymId={selectedGymId}
          currentCost={operatingCost}
          currentWeightages={buildWeightageMap(trainerTargets)}
          onSaved={fetchData}
        />

        {/* Weightage Editor Dialog */}
        <WeightageEditorDialog
          open={editWeightagesOpen}
          onOpenChange={setEditWeightagesOpen}
          gymId={selectedGymId}
          trainerTargets={trainerTargets}
          operatingCost={operatingCost}
          onSaved={fetchData}
        />
      </div>
    </PullToRefresh>
  );
}

/**
 * Build weightage map from trainer targets array.
 */
function buildWeightageMap(trainerTargets) {
  const map = {};
  (trainerTargets || []).forEach((t) => {
    map[t.trainer_id] = t.weightage;
  });
  return map;
}

/**
 * EditOperatingCostDialog - admin can update operating cost.
 */
function EditOperatingCostDialog({ open, onOpenChange, gymId, currentCost, currentWeightages, onSaved }) {
  const [cost, setCost] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCost(currentCost > 0 ? String(currentCost) : '');
    }
  }, [open, currentCost]);

  const handleSave = async () => {
    const numCost = parseFloat(cost);
    if (isNaN(numCost) || numCost < 0) {
      toast.error('Please enter a valid cost');
      return;
    }

    setSaving(true);
    try {
      await axiosInstance.put(`/fm/targets/${gymId}`, {
        operating_cost: numCost,
        trainer_weightages: currentWeightages,
      });
      toast.success('Operating cost updated');
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Edit Operating Cost</DialogTitle>
          <DialogDescription>
            PT Target will be auto-calculated as 2x this value.
          </DialogDescription>
        </DialogHeader>

        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">
            Monthly Operating Cost (Rs)
          </label>
          <input
            type="number"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            placeholder="e.g. 600000"
            min="0"
            className="w-full px-3 py-2.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
          {cost && !isNaN(parseFloat(cost)) && (
            <p className="text-xs text-slate-500 mt-2">
              PT Target: {formatCurrency(parseFloat(cost) * 2)}
            </p>
          )}
        </div>

        <DialogFooter>
          <button
            onClick={handleSave}
            disabled={saving || !cost}
            className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * WeightageEditorDialog - edit trainer weightages with sum validation.
 */
function WeightageEditorDialog({ open, onOpenChange, gymId, trainerTargets, operatingCost, onSaved }) {
  const [weightages, setWeightages] = useState({});
  const [saving, setSaving] = useState(false);

  // Initialize weightages from trainer targets when dialog opens
  useEffect(() => {
    if (open && trainerTargets.length > 0) {
      const initial = {};
      trainerTargets.forEach((t) => {
        initial[t.trainer_id] = t.weightage;
      });
      setWeightages(initial);
    }
  }, [open, trainerTargets]);

  const totalWeightage = Object.values(weightages).reduce((sum, w) => sum + (parseFloat(w) || 0), 0);
  const isValidTotal = totalWeightage >= 99 && totalWeightage <= 101;

  const handleWeightageChange = (trainerId, value) => {
    setWeightages((prev) => ({ ...prev, [trainerId]: parseFloat(value) || 0 }));
  };

  const resetToRankBased = () => {
    const reset = {};
    trainerTargets.forEach((t) => {
      const rank = t.rank || 4;
      reset[t.trainer_id] = DEFAULT_RANK_WEIGHTAGES[rank] || 20;
    });
    setWeightages(reset);
  };

  const handleSave = async () => {
    if (!isValidTotal) {
      toast.error(`Weightages must sum to 100 (currently ${totalWeightage.toFixed(1)})`);
      return;
    }

    setSaving(true);
    try {
      await axiosInstance.put(`/fm/targets/${gymId}`, {
        operating_cost: operatingCost,
        trainer_weightages: weightages,
      });
      toast.success('Weightages updated');
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update weightages');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Edit Trainer Weightages</DialogTitle>
          <DialogDescription>
            Set how the PT target is distributed across trainers.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[50vh] overflow-y-auto">
          {[...trainerTargets]
            .sort((a, b) => (a.rank || 99) - (b.rank || 99))
            .map((trainer) => (
              <div key={trainer.trainer_id} className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">
                    {trainer.trainer_name}
                    {trainer.rank > 0 && (
                      <span className="text-xs text-slate-400 ml-1">R{trainer.rank}</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={weightages[trainer.trainer_id] ?? trainer.weightage}
                    onChange={(e) => handleWeightageChange(trainer.trainer_id, e.target.value)}
                    min="0"
                    max="100"
                    step="1"
                    className="w-16 px-2 py-1.5 text-sm text-center border rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                  <span className="text-xs text-slate-400">%</span>
                </div>
              </div>
            ))}
        </div>

        {/* Running total */}
        <div
          className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium ${
            isValidTotal
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-700'
          }`}
        >
          <span>Total:</span>
          <span>
            {totalWeightage.toFixed(1)}%
            {!isValidTotal && ` (need 100%)`}
          </span>
        </div>

        {/* Reset to rank-based defaults */}
        <button
          onClick={resetToRankBased}
          className="text-xs text-blue-600 underline"
        >
          Reset to Rank-Based (30/27/23/20)
        </button>

        <DialogFooter>
          <button
            onClick={handleSave}
            disabled={saving || !isValidTotal}
            className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Weightages'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Simple refreshing spinner for pull-to-refresh.
 */
function RefreshingSpinner() {
  return (
    <div className="flex justify-center py-3">
      <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}
